import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { 
    Chart as ChartJS, 
    ArcElement, 
    Tooltip, 
    Legend, 
    RadialLinearScale,
    PointElement,
    LineElement
} from 'chart.js';
import { Pie, Radar, Doughnut } from 'react-chartjs-2';
import backIcon from "../assets/backIcon.png";
import downloadIcon from "../assets/export-icon.png";
import jsPDF from 'jspdf';
import Cookies from 'js-cookie';
import html2canvas from 'html2canvas';
import {gsap} from 'gsap';

import CheckMark from "../assets/Check mark.svg";
import CrossMark from "../assets/Cross mark.svg";

ChartJS.register(
    ArcElement, 
    Tooltip, 
    Legend, 
    RadialLinearScale,
    PointElement,
    LineElement
);

// Removed OpenAI import and initialization - now using backend API

const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const base64String = reader.result.split(',')[1];
            resolve({
                base64: base64String,
                filename: file.name
            });
        };
        reader.onerror = (error) => reject(error);
    });
};

const postAnalysisToAPI = async (cvFile, mLetterfile, linkedInURL, linkedInData, analysisResults) => {
    try {
        // Convert files to base64
        const cvBase64 = await convertFileToBase64(cvFile);
        const mlBase64 = await convertFileToBase64(mLetterfile);

        const requestBody = {
            CvFile: {
                base64: cvBase64.base64,
                filename: cvBase64.filename
            },
            MLetterFile: {
                base64: mlBase64.base64,
                filename: mlBase64.filename
            },
            linkedInURL: linkedInURL,
            linkedInData: {
                firstName: linkedInData.firstName,
                lastName: linkedInData.lastName,
                headline: linkedInData.headline,
                industryName: linkedInData.industryName,
                locationName: linkedInData.locationName,
                experience: linkedInData.experience.map(exp => ({
                    title: exp.title,
                    company: exp.company,
                    start_date: exp.start_date,
                    end_date: exp.end_date
                })),
                skills: linkedInData.skills,
                languages: linkedInData.languages.map(lang => ({
                    name: lang.name,
                    proficiency: lang.proficiency
                })),
                displayPictureUrl:  `${linkedInData.displayPictureUrl}${linkedInData.img_800_800}`
            },
            Analysis: {
                summary: {
                    keyTraits: analysisResults.summary.keyTraits,
                    dominantTraits: analysisResults.summary.dominantTraits, // Ensure this is a string
                    jobMatch: analysisResults.summary.jobMatch // Ensure this is a string
                },
                bigFive: {
                    openness: {
                        score: analysisResults.bigFive.openness.score,
                        level: analysisResults.bigFive.openness.level,
                        interpretation: analysisResults.bigFive.openness.interpretation
                    },
                    conscientiousness: {
                        score: analysisResults.bigFive.conscientiousness.score,
                        level: analysisResults.bigFive.conscientiousness.level,
                        interpretation: analysisResults.bigFive.conscientiousness.interpretation
                    },
                    extraversion: {
                        score: analysisResults.bigFive.extraversion.score,
                        level: analysisResults.bigFive.extraversion.level,
                        interpretation: analysisResults.bigFive.extraversion.interpretation
                    },
                    agreeableness: {
                        score: analysisResults.bigFive.agreeableness.score,
                        level: analysisResults.bigFive.agreeableness.level,
                        interpretation: analysisResults.bigFive.agreeableness.interpretation
                    },
                    neuroticism: {
                        score: analysisResults.bigFive.neuroticism.score,
                        level: analysisResults.bigFive.neuroticism.level,
                        interpretation: analysisResults.bigFive.neuroticism.interpretation
                    },
                    workBehavior: analysisResults.bigFive.workBehavior // Ensure this is a string
                },
                disc: {
                    dominance: {
                        score: analysisResults.disc.dominance.score,
                        interpretation: analysisResults.disc.dominance.interpretation
                    },
                    influence: {
                        score: analysisResults.disc.influence.score,
                        interpretation: analysisResults.disc.influence.interpretation
                    },
                    steadiness: {
                        score: analysisResults.disc.steadiness.score,
                        interpretation: analysisResults.disc.steadiness.interpretation
                    },
                    conscientiousness: {
                        score: analysisResults.disc.conscientiousness.score,
                        interpretation: analysisResults.disc.conscientiousness.interpretation
                    },
                    primaryStyle: analysisResults.disc.primaryStyle, // Ensure this is a string
                    workPerformance: analysisResults.disc.workPerformance // Ensure this is a string
                },
                mbti: {
                    type: analysisResults.mbti.type, // Ensure this is a string
                    dimensions: {
                        extraversion_introversion: {
                            preference: analysisResults.mbti.dimensions.extraversion_introversion.preference,
                            score: analysisResults.mbti.dimensions.extraversion_introversion.score,
                            interpretation: analysisResults.mbti.dimensions.extraversion_introversion.interpretation
                        },
                        sensing_intuition: {
                            preference: analysisResults.mbti.dimensions.sensing_intuition.preference,
                            score: analysisResults.mbti.dimensions.sensing_intuition.score,
                            interpretation: analysisResults.mbti.dimensions.sensing_intuition.interpretation
                        },
                        thinking_feeling: {
                            preference: analysisResults.mbti.dimensions.thinking_feeling.preference,
                            score: analysisResults.mbti.dimensions.thinking_feeling.score,
                            interpretation: analysisResults.mbti.dimensions.thinking_feeling.interpretation
                        },
                        judging_perceiving: {
                            preference: analysisResults.mbti.dimensions.judging_perceiving.preference,
                            score: analysisResults.mbti.dimensions.judging_perceiving.score,
                            interpretation: analysisResults.mbti.dimensions.judging_perceiving.interpretation
                        }
                    },
                    workStyle: analysisResults.mbti.workStyle, // Ensure this is a string
                    strengths: analysisResults.mbti.strengths, // Ensure this is an array of strings
                    challenges: analysisResults.mbti.challenges // Ensure this is an array of strings
                },
                riasec: {
                    realistic: {
                        score: analysisResults.riasec.realistic.score,
                        description: analysisResults.riasec.realistic.description // Ensure this is a string
                    },
                    investigative: {
                        score: analysisResults.riasec.investigative.score,
                        description: analysisResults.riasec.investigative.description // Ensure this is a string
                    },
                    artistic: {
                        score: analysisResults.riasec.artistic.score,
                        description: analysisResults.riasec.artistic.description // Ensure this is a string
                    },
                    social: {
                        score: analysisResults.riasec.social.score,
                        description: analysisResults.riasec.social.description // Ensure this is a string
                    },
                    enterprising: {
                        score: analysisResults.riasec.enterprising.score,
                        description: analysisResults.riasec.enterprising.description // Ensure this is a string
                    },
                    conventional: {
                        score: analysisResults.riasec.conventional.score,
                        description: analysisResults.riasec.conventional.description // Ensure this is a string
                    },
                    primaryType: analysisResults.riasec.primaryType, // Ensure this is a string
                    secondaryType: analysisResults.riasec.secondaryType // Ensure this is a string
                },
                recommendations: {
                    dos: analysisResults.recommendations.dos, // Ensure this is an array of strings
                    donts: analysisResults.recommendations.donts, // Ensure this is an array of strings
                    bestWorkCulture: analysisResults.recommendations.bestWorkCulture, // Ensure this is a string
                    suitableJobs: analysisResults.recommendations.suitableJobs, // Ensure this is an array of strings
                    developmentPoints: analysisResults.recommendations.developmentPoints // Ensure this is an array of strings
                }
            }
        };

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/report`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${Cookies.get('token')}`,
                'Content-Type': 'application/json',
                'accept': 'application/json'
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            throw new Error('Failed to post analysis to API');
        }

        return await response.json();
    } catch (error) {
        console.error('Error posting analysis to API:', error);
        throw error;
    }
};

function Analysis({cvFile, mLetterfile, linkedInURL, linkedInData, isGeneratingPDF, setIsGeneratingPDF}) {
    const navigate = useNavigate();
    const location = useLocation();
    const [analyzing, setAnalyzing] = useState(true);
    const [progress, setProgress] = useState(0);
    const [analysisResults, setAnalysisResults] = useState(null);
    const [error, setError] = useState(null);
    const [generatingPDF, setGeneratingPDF] = useState(false);

    const discColors = [ 
        '#004cff', 
        '#008121', 
        '#ffee00',
        '#ff8d00', 
        '#e50000',
        '#760188', 
    ]
    
    const colors = [ 
        '#004cff', 
        '#008121', 
        '#ffee00',
        '#ff8d00', 
        '#e50000',
        '#760188', 
    ]

    const bigFiveColors = [
        '#004cff', 
        '#008121', 
        '#ffee00',
        '#ff8d00', 
        '#e50000',
        '#760188', 
    ]

    const [bigFiveData, setBigFiveData] = useState({
        labels: ['Openness', 'Conscientiousness', 'Extraversion', 'Agreeableness', 'Neuroticism'],
        datasets: [{
            label: 'Big Five Score',
            data: [0, 0, 0, 0, 0],
            backgroundColor: [
                '#004cff', 
        '#008121', 
        '#ffee00',
        '#ff8d00', 
        '#e50000',
        '#760188', 
            ],
            borderColor: 'rgba(255, 255, 255, 1)',
            borderWidth: 2,
        }]
    });

    const [discData, setDiscData] = useState({
        labels: ['Dominantie', 'Invloed', 'Stabiliteit', 'Consciëntieusheid'],
        datasets: [
            {
                data: [25, 30, 20, 25],
                backgroundColor: [
                    '#004cff', 
                    '#008121', 
                    '#ffee00',
                    '#ff8d00', 
                    '#e50000',
                    '#760188', 
                ],
                borderColor: [
                    '#ffffff',
                    '#ffffff',
                    '#ffffff',
                    '#ffffff',
                ],
                borderWidth: 2,
            },
        ],
    });

    const [riasecData, setRiasecData] = useState({
        labels: ['Realistisch', 'Onderzoekend', 'Artistiek', 'Sociaal', 'Ondernemend', 'Conventioneel'],
        datasets: [
            {
                label: 'RIASEC Score',
                data: [2, 5, 3, 4, 3, 5],
                backgroundColor: 'rgba(54, 162, 235, 0.2)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 2,
                pointBackgroundColor: [
                    '#004cff', 
                    '#008121', 
                    '#ffee00',
                    '#ff8d00', 
                    '#e50000',
                    '#760188', 
                ],
                pointBorderColor: '#fff',
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: 'rgba(54, 162, 235, 1)',
                pointRadius: 5,
            }
        ]
    });

    const [mbtiData, setMbtiData] = useState({
        labels: ['Extraversiaaaon (E)', 'Intuition (N)', 'Thinking (T)', 'Judging (J)'],
        datasets: [{
            label: 'MBTI Score',
            data: [75, 68, 82, 70],
            backgroundColor: 'rgba(54, 162, 235, 0.2)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 2,
                pointBackgroundColor: [
                    '#004cff', 
                    '#008121', 
                    '#ffee00',
                    '#ff8d00', 
                    '#e50000',
                    '#760188', 
                ],
                pointBorderColor: '#fff',
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: 'rgba(54, 162, 235, 1)',
                pointRadius: 5,
        }]
    });

    const processFiles = async () => {
        try {
            if (!cvFile || !mLetterfile) {
                throw new Error("Missing required files");
            }

            setProgress(20);

            const formData = new FormData();
            formData.append('cv_file', cvFile);
            formData.append('cover_letter_file', mLetterfile);
            formData.append('linkedin_data', JSON.stringify(linkedInData));
            formData.append('linkedin_url', linkedInURL);

            setProgress(50);


            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/analysis/admin/complete-analysis`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${Cookies.get('token')}`
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error('Backend analysis failed');
            }

            const result = await response.json();
            const analysisResult = result.data; 

            setProgress(80);

            await setAnalysisResults(analysisResult);
            
            const newDiscData = {
                labels: ['Dominance', 'Influence', 'Steadiness', 'Conscientiousness'],
                datasets: [{
                    data: [
                        analysisResult.disc.dominance.score,
                        analysisResult.disc.influence.score,
                        analysisResult.disc.steadiness.score,
                        analysisResult.disc.conscientiousness.score
                    ],
                    backgroundColor: [
                        '#004cff', 
                    '#008121', 
                    '#ffee00',
                    '#ff8d00', 
                    '#e50000',
                    '#760188', 
                    ],
                    borderColor: [
                        '#ffffff',
                        '#ffffff',
                        '#ffffff',
                        '#ffffff',
                    ],
                    borderWidth: 2,
                }]
            };

            const newRiasecData = {
                labels: ['Realistic', 'Investigative', 'Artistic', 'Social', 'Enterprising', 'Conventional'],
                datasets: [{
                    label: 'RIASEC Score',
                    data: [
                        analysisResult.riasec.realistic.score,
                        analysisResult.riasec.investigative.score,
                        analysisResult.riasec.artistic.score,
                        analysisResult.riasec.social.score,
                        analysisResult.riasec.enterprising.score,
                        analysisResult.riasec.conventional.score
                    ],
                    backgroundColor: 'rgba(54, 162, 235, 0.2)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 2,
                    pointBackgroundColor: [
                        '#004cff', 
                    '#008121', 
                    '#ffee00',
                    '#ff8d00', 
                    '#e50000',
                    '#760188', 
                    ],
                    pointBorderColor: '#fff',
                    pointHoverBackgroundColor: '#fff',
                    pointHoverBorderColor: 'rgba(54, 162, 235, 1)',
                    pointRadius: 5,
                }]
            };

            const newMbtiData = {
                labels: ['Extraversion (E)', 'Intuition (N)', 'Thinking (T)', 'Judging (J)'],
                datasets: [{
                    label: 'MBTI Score',
                    data: [
                        analysisResult.mbti.dimensions.extraversion_introversion.score,
                        analysisResult.mbti.dimensions.sensing_intuition.score,
                        analysisResult.mbti.dimensions.thinking_feeling.score,
                        analysisResult.mbti.dimensions.judging_perceiving.score
                    ],
                    backgroundColor: 'rgba(54, 162, 235, 0.2)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 2,
                    pointBackgroundColor: [
                        '#004cff', 
                        '#008121', 
                        '#ffee00',
                        '#ff8d00', 
                        '#e50000',
                        '#760188', 
                    ],
                    pointBorderColor: '#fff',
                    pointHoverBackgroundColor: '#fff',
                    pointHoverBorderColor: 'rgba(54, 162, 235, 1)',
                    pointRadius: 5,
                }]
            };

             // Update Big Five chart data
             const bigFiveScores = Object.entries(analysisResult.bigFive)
             .filter(([trait]) => trait !== 'workBehavior')
             .map(([_, data]) => data.score);
           
           setBigFiveData(prev => ({
             ...prev,
             datasets: [{
               ...prev.datasets[0],
               data: bigFiveScores
             }]
           }));

            setDiscData(newDiscData);
            setRiasecData(newRiasecData);
            setMbtiData(newMbtiData);
            
            setProgress(100);
            setAnalyzing(false);
            
        } catch (err) {
            console.error('Analysis Error:', err);
            setError(err.message);
            setAnalyzing(false);
        }
    };
    

    useEffect(() => {
        let progressInterval;
        if (analyzing) {
            processFiles();
            progressInterval = setInterval(() => {
                setProgress((prev) => {
                    if (prev >= 95) {
                        clearInterval(progressInterval);
                        return prev;
                    }
                    return prev + 1;
                });
            }, 200);
        }
        return () => clearInterval(progressInterval);
    }, [analyzing]);

    useEffect(() => {
        const saveAnalysisToAPI = async () => {
            if (analysisResults) {
                try {
                    await postAnalysisToAPI(cvFile, mLetterfile, linkedInURL, linkedInData, analysisResults);
                    console.log('Analysis successfully saved to API');
                } catch (error) {
                    console.error('Failed to save analysis to API:', error);
                    setError('Failed to save analysis to API');
                }
            }
        };

        saveAnalysisToAPI();
    }, [analysisResults, cvFile, mLetterfile, linkedInURL, linkedInData]);

    //Chartsss
    const riasecOptions = {
        scales: {
            r: {
                angleLines: {
                    display: true
                },
                suggestedMin: 0,
                suggestedMax: 6,
                ticks: {
                    stepSize: 1
                }
            }
        },
        plugins: {
            legend: {
                display: false
            }
        }
    };

    const chartOptions = {
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    padding: 20,
                    usePointStyle: true,
                }
            }
        }
    };

    const generatePDF = async () => {
        const input = document.getElementById("analysis-report"); 
        setGeneratingPDF(true);
        setIsGeneratingPDF(true);
        
        try {
            await new Promise(resolve => setTimeout(resolve, 300));

            const canvas = await html2canvas(input, { useCORS: true }); 
            const imgData = canvas.toDataURL("image/png"); 
            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "px",
                format: [canvas.width, canvas.height],
                putOnlyUsedFonts: true,
                floatPrecision: 16 
            });

            pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height); 

            pdf.save(`${linkedInData.firstName}_${linkedInData.lastName}_Analysis_Report.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            setError('Failed to generate PDF report');
        } finally {
            setIsGeneratingPDF(false);
            setGeneratingPDF(false);
        }
    };

    useEffect(() => {
        if(analysisResults && !analyzing){
         const sections = document.querySelectorAll('.animate-text');
         const tl = gsap.timeline();
     
         sections.forEach(section => {
             tl.to(section, 
                 { 
                     stagger: 0.2,
                     duration: 0.2, 
                     opacity: 1, 
                     y: 0, 
                     ease: "back",
                 }
             );
         });
        }
     }, [analysisResults, analyzing]);

    return (
      <div className="m-0 text-left pb-10">
        {analyzing && (
            <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
                <div className="bg-white p-8 rounded-lg w-96">
                    <h2 className="text-2xl font-bold mb-4">Analyzing Data...</h2>
                    <div className="flex gap-2 items-center">
                        <div className="relative w-full h-2 bg-gray-200 rounded">
                            <div
                                className="absolute h-full bg-primary rounded"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                        <span>{progress}%</span>
                    </div>
                </div>
            </div>
        )}

        {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                <strong className="font-bold">Error!</strong>
                <span className="block sm:inline"> {error}</span>
            </div>
        )}

        {!isGeneratingPDF && (
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/upload")}>
            <img src={backIcon} alt="back" className="w-5 h-5" />
            <p>Back</p>
        </div>
        )}

        <div className="flex items-center justify-between mb-4 mt-8 gap-4">
            <h1 className="text-5xl font-bold">Personality Analysis Report</h1>
            {!isGeneratingPDF && (
            <button 
                className="border px-4 py-2 rounded-md flex items-center gap-2 bg-default"
                onClick={generatePDF}
            >
                <img src={downloadIcon} alt="download" className="w-4 h-4" />
                <p className="text-white truncate">
                    {generatingPDF ? 'Generating PDF...' : 'Download as PDF'}
                </p>
            </button>
            )}
        </div>

        <main id="analysis-report" className="flex flex-col gap-8">
            {analysisResults && (
                <>
                    <div className="mt-4 border p-4 rounded-md w-full h-fi animate-text">
                        <div className="flex items-center gap-4">
                            <img src={`${linkedInData.displayPictureUrl}${linkedInData.img_800_800}`} className="w-16 h-16 rounded-full animate-text"/>
                            <span>
                                <h2 className="text-2xl font-bold animate-text">{`${linkedInData.firstName} ${linkedInData.lastName}`}</h2>
                                <p className="text-gray-500 animate-text">{linkedInData.headline}</p>
                            </span>
                        </div>
                        <div className="mt-4">
                            <h3 className="text-gray-500 font-bold animate-text">Key Traits</h3>
                            <p className="capitalize animate-text">{analysisResults.summary.keyTraits.join(", ")}</p>
                        </div>
                    </div>

                    <section className="flex flex-col gap-8">
                        <div className="flex flex-col animate-text">
                            <h2 className="text-xl font-bold">Big Five Analysis (OCEAN Model)</h2>
                            <div className="flex-1 border rounded-md w-full h-fit mt-2 flex flex-col gap-4">
                                <div className="w-full h-[300px] flex items-center justify-center mt-4 animate-text">
                                    <Doughnut data={bigFiveData} options={chartOptions} />
                                </div>
                                <div className="grid grid-cols-2 gap-2 p-4 ">
                                    {Object.entries(analysisResults.bigFive).map(([trait, data], index) => (
                                        trait !== 'workBehavior' && (
                                           <div className="animate-text">
                                           <span key={trait}>
                                                <h3 className="text-gray-500 font-bold capitalize">{trait}</h3>
                                                <div className="flex justify-between gap-2 items-center mt-2">
                                                    <h3 className=" font-semibold capitalize">{data.level}</h3>
                                            <p className="text-gray-500 text-sm">{data.score}%</p>
                                                </div>
                                                <div className="relative w-full h-2 bg-gray-200 rounded mt-2">
                                            <div
                                                    className="absolute h-full bg-default rounded"
                                                    style={{ width: `${(data.score)}%`, backgroundColor: bigFiveColors[index] }}
                                                />
                                            </div>
                                                <p className="text-sm text-gray-600 mt-4">{data.interpretation}</p>
                                            </span>
                                           </div>
                                        )
                                    ))}
                                </div>
                                <div className="mt-2 border-top p-4 animate-text">
                                    <h3 className="text-gray-500 font-bold">Work Behavior</h3>
                                    <p className="animate-text">{analysisResults.bigFive.workBehavior}</p>
                                </div>
                            </div>
                        </div>

                        <section className="grid grid-cols-2 gap-4">
                            <div className="flex flex-col animate-text">
                                <h2 className="text-xl font-bold">DISC Model</h2>
                                <div className="flex-1 border p-4 rounded-md w-full h-fit mt-2 flex flex-col gap-4">
                                    <div className="w-full h-[300px] flex items-center justify-center animate-text">
                                        <Pie data={discData} options={chartOptions} />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 mt-4">
                                        {Object.entries(analysisResults.disc).map(([trait, data], index) => (
                                            trait !== 'primaryStyle' && trait !== 'workPerformance' && (
                                              <div className="animate-text">
                                              <span key={trait}>
                                                    <div className="flex justify-between gap-2 items-center mt-2">
                                                    <h3 className="text-gray-500 font-bold capitalize">{trait}</h3>
                                            <p className="text-gray-500 text-sm">{data.score}%</p>
                                                </div>
                                                    <div className="relative w-full h-2 bg-gray-200 rounded mt-2">
                                            <div
                                                    className="absolute h-full bg-default rounded"
                                                    style={{ width: `${(data.score)}%`, backgroundColor: discColors[index] }}
                                                />
                                            </div>
                                                    <p className="text-sm text-gray-600 mt-4">{data.interpretation}</p>
                                                </span>
                                              </div>
                                            )
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col animate-text">
                                <h2 className="text-xl font-bold">RIASEC Model</h2>
                                <div className="flex-1 border p-4 rounded-md w-full h-fit mt-2 flex flex-col gap-4">
                                    <div className="w-full h-[300px] flex items-center justify-center animate-text">
                                        <Radar data={riasecData} options={riasecOptions} />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4 mt-4">
                                        {Object.entries(analysisResults.riasec).map(([type, data], index) => (
                                            type !== 'primaryType' && type !== 'secondaryType' && (
                                                <div className="animate-text">
                                                <span key={type}>
                                                    
                                                    <div className="flex justify-between gap-2 items-center mt-2">
                                                    <h3 className="text-gray-500 font-bold capitalize">{type}</h3>
                                            <p className="text-gray-500 text-sm">{data.score}/5</p>
                                                </div>
                                                    <div className="relative w-full h-2 bg-gray-200 rounded mt-2">
                                            <div
                                                    className="absolute h-full bg-default rounded"
                                                    style={{ width: `${(data.score)*20}%`, backgroundColor: colors[index] }}
                                                />
                                            </div>
                                                    <p className="text-sm text-gray-600 mt-4">{data.description}</p>
                                                </span>
                                                </div>
                                            )
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </section>

                        <div className="flex flex-col animate-text">
                            <h2 className="text-xl font-bold">MBTI Analysis</h2>
                            <div className="flex-1 border p-4 rounded-md w-full h-fit mt-2 flex flex-col gap-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <div className="w-full h-[300px] flex items-center justify-center animate-text">
                                            <Radar data={mbtiData} options={riasecOptions} />
                                        </div>
                                        <div className="text-center mt-4">
                                            <h3 className="text-2xl font-bold">{analysisResults.mbti.type}</h3>
                                            <p className="text-gray-600">Your MBTI Type</p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-4 justify-center">
                                        <div className="animate-text">
                                            <h3 className="text-gray-500 font-bold">Work Style</h3>
                                            <p>{analysisResults.mbti.workStyle}</p>
                                        </div>
                                        <div className="animate-text">
                                            <h3 className="text-gray-500 font-bold">Strengths</h3>
                                            <ul className="ml-7 list-disc">
                                                {analysisResults.mbti.strengths.map((strength, index) => (
                                                    <li key={index}>{strength}</li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="animate-text">
                                            <h3 className="text-gray-500 font-bold">Challenges</h3>
                                            <ul className="ml-7 list-disc">
                                                {analysisResults.mbti.challenges.map((challenge, index) => (
                                                    <li key={index}>{challenge}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    {Object.entries(analysisResults.mbti.dimensions).map(([dimension, data], index) => (
                                        <div key={dimension} className="rounded-md animate-text">
                                            <h3 className="text-gray-500 font-bold capitalize">
                                                {dimension.replace('_', ' - ')}
                                            </h3>
                                            <div className="flex justify-between gap-2 items-center mt-2">
                                            <p className="font-semibold">Preference: {data.preference}</p>
                                            <p className="text-gray-500 text-sm">{data.score}%</p>
                                                </div>
                                            <div className="relative w-full h-2 bg-gray-200 rounded mt-2">
                                            <div
                                                    className="absolute h-full bg-default rounded"
                                                    style={{ width: `${data.score}%`, backgroundColor: colors[index] }}
                                                />
                                            </div>
                                            <p className="text-sm text-gray-600 mt-4">{data.interpretation}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col pb-20 animate-text">
                            <h2 className="text-xl font-bold">Recommendations</h2>
                            <div className="flex-1 border rounded-md w-full h-fit mt-2 flex flex-col gap-4">
                                <div className="flex flex-col gap-4">
                                   <div className="grid grid-cols-2 gap-4 p-4">
                                   <div className="animate-text">
                                        <h3 className="text-gray-500 font-bold flex items-center gap-2 ">
                                            <span className="text-green-500 text-sm"><img src={CheckMark} alt="check" className="w-4 h-4" /></span> Do's:
                                        </h3>
                                        <ul className="ml-7 list-disc">
                                            {analysisResults.recommendations.dos.map((item, index) => (
                                                <li key={index}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="animate-text">
                                        <h3 className="text-gray-500 font-bold flex items-center gap-2">
                                            <span className="text-red-500 text-sm"><img src={CrossMark} alt="cross" className="w-4 h-4" /></span> Don'ts:
                                        </h3>
                                        <ul className="ml-7 list-disc">
                                            {analysisResults.recommendations.donts.map((item, index) => (
                                                <li key={index}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                   </div>
                                    <div className="border-top p-4">
                                        <h3 className="text-gray-500 font-bold">Best Work Culture</h3>
                                        <p className="animate-text">{analysisResults.recommendations.bestWorkCulture}</p>
                                    </div>
                                    <div className="border-top p-4">
                                        <h3 className="text-gray-500 font-bold">Suitable Jobs</h3>
                                        <p className="animate-text">{analysisResults.recommendations.suitableJobs.join(", ")}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </>
            )}
        </main>
      </div>
    );
  }
  
  export default Analysis; 